import Component from '../../components/Component';
import query from './query';
import Loading from '../../components/Loading';
import { v4 as uuid } from 'uuid';
import DataTable from '../../components/Datatable';
import { COLUMNS, getStrength } from './keys';
import { scrollForTable } from '../../components/Datatable/utils';
import React from 'react';
import AdditionalBox from './additionalBox';
import { withTranslation } from 'react-i18next';

function findParts(acc, curr, ids) {
    const parts = curr.path.split('/');
    if (parts.length > 1) {
        const parent = parts.shift();
        return findParts(
            acc.nodes.find((item) => item.name === parent),
            { ...curr, path: parts.join('/') },
            ids,
        );
    } else {
        const id = uuid();
        ids.push(id);
        if (acc.nodes) {
            acc.nodes.push({ ...curr, name: parts[0], id });
        } else {
            acc.nodes = [{ ...curr, name: parts[0], id }];
        }
    }
    return acc;
}

function createTree(data) {
    if (!data || data?.length === 0) return { nodes: [], ids: [] };
    const ids = [];
    const collectedData = data.reduce((acc, curr) => {
        const levelParts = curr.own_level.slice(0, -1).split('/');
        levelParts.shift();
        if (levelParts.length === 1) {
            const id = uuid();
            ids.push(id);
            acc.push({ ...curr, name: levelParts[0], id });
        } else if (levelParts.length >= 2) {
            const parent = levelParts.shift();
            findParts(
                acc.find((item) => item.name === parent),
                { ...curr, path: levelParts.join('/') },
                ids,
            );
        }
        return acc;
    }, []);
    const allData = [
        {
            name: 'All Categories',
            own_unique_pages: null,
            own_sum_impressions: null,
            own_sum_clicks: null,
            own_avg_ctr: null,
            comp_unique_pages: null,
            comp_sum_impressions: null,
            comp_sum_clicks: null,
            comp_avg_ctr: null,
            nodes: collectedData.map((item) => ({ ...item, haveStrength: true })),
            id: 'all_categories',
        },
    ];
    return { nodes: allData, ids };
}

function getNestedView(data, length) {
    if (!data || data.length === 0) return [];
    const collectedView = data
        .filter((item) => item.own_level.split('/').length === length)
        .map((item) => ({ ...item, name: `all${item.own_level}`, id: uuid(), haveStrength: true }));
    return collectedView;
}

class Analysis extends Component {
    constructor(props) {
        super();
        this.query = query;

        this.chooseView = this.chooseView.bind(this);
        this.getViewData = this.getViewData.bind(this);
        this.ids = React.createRef();

        this.images = {
            strong: props.image_strong,
            striking_distance: props.image_striking_distance,
            weak: props.image_weak,
        };
    }

    componentDidUpdate(prevProps, prevState) {
        if (JSON.stringify(prevState.data) !== JSON.stringify(this.state.data)) {
            const { nodes, ids } = createTree(this.state.data);
            this.ids.current = ids;
            this.setState({
                nodes,
                ids,
            });
        }
    }

    searchFunction(data, search) {
        const filteredData = data?.filter((item) =>
            item.own_level?.toLowerCase().includes(search?.toLowerCase()),
        );
        const withParents = data.filter((item) => {
            const parent = filteredData.find((state) =>
                state.own_level?.toLowerCase().includes(item.own_level?.toLowerCase()),
            );
            if (parent) return true;

            return false;
        });

        const { nodes, ids } = createTree(withParents);
        this.ids.current = ids;

        return nodes;
    }

    chooseView(view) {
        this.setState({ view });
    }

    getViewData(nodes) {
        switch (this.state.view) {
            case 1:
                return nodes;
            case 2: {
                const data = getNestedView(this.state.data, 4);
                return data;
            }
            case 3: {
                const data = getNestedView(this.state.data, 5);
                return data;
            }
            default:
                return nodes;
        }
    }

    getPercentOwnImpCompImp(item) {
        const impPerPage = item.own_sum_impressions / item.own_unique_pages;
        const compImpPerPage = item.comp_sum_impressions / item.comp_unique_pages;
        const value = (impPerPage - compImpPerPage) / compImpPerPage;
        return value;
    }

    getPercentOwnClickCompClick(item) {
        const own_ClicksPerPage = item.own_sum_clicks / item.own_unique_pages;
        const comp_ClicksPerPage = item.comp_sum_clicks / item.comp_unique_pages;
        const value = (own_ClicksPerPage - comp_ClicksPerPage) / comp_ClicksPerPage;
        return value;
    }

    render() {
        const { t } = this.props;

        if (this.state.loading) {
            return <Loading padding={10} />;
        }

        if (!this.state.data || !this.state.data[0])
            return <div className="p-10">There is no data</div>;

        const sortFns = {
            category: (array) => array.sort((a, b) => a.name.localeCompare(b.name)),
            own_unique_pages: (array) =>
                array.sort((a, b) => a.own_unique_pages - b.own_unique_pages),
            own_avg_ctr: (array) => array.sort((a, b) => a.own_avg_ctr - b.own_avg_ctr),
            Own_ClicksPerPage: (array) => array.sort((a, b) => a.own_sum_clicks - b.own_sum_clicks),
            Own_impPerPage: (array) =>
                array.sort((a, b) => a.own_sum_impressions - b.own_sum_impressions),
            strength: (array) => array.sort((a, b) => getStrength(a) - getStrength(b)),
            confidence: (array) => array.sort((a, b) => a.comp_unique_pages - b.comp_unique_pages),
            'Own_ctr-comp_ctr': (array) =>
                array.sort(
                    (a, b) =>
                        a['own_avg_ctr'] -
                        a['comp_avg_ctr'] -
                        (b['own_avg_ctr'] - b['comp_avg_ctr']),
                ),
            'percent-ownImp-compimp': (array) =>
                array.sort((a, b) => {
                    const a_value = this.getPercentOwnImpCompImp(a);
                    const b_value = this.getPercentOwnImpCompImp(b);

                    return a_value - b_value;
                }),
            'percent-ownClick-compClick': (array) =>
                array.sort((a, b) => {
                    const a_value = this.getPercentOwnClickCompClick(a);
                    const b_value = this.getPercentOwnClickCompClick(b);
                    return a_value - b_value;
                }),
        };

        const extraBtns = () => (
            <>
                <div className="table-header-subtitle">
                    <div>
                        {t('discover.competitive_analysis.subtitle')}
                        <div className="table-header-subtitle-boxes">
                            <div className="d-flex flex-column gap-5">
                                <div>{t('discover.competitive_analysis.subtitle_1')}</div>
                                <div>{t('discover.competitive_analysis.subtitle_2')}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );

        const additionalBox = (hasTree, tree) => (
            <AdditionalBox
                ids={this.ids}
                view={this.state.view}
                hasTree={hasTree}
                tree={tree}
                chooseView={this.chooseView}
                t={t}
            />
        );

        const data = this.getViewData(this.state.nodes);
        return (
            <DataTable
                nodes={data}
                sortFns={sortFns}
                openedItemIds={['all_categories']}
                COLUMNS={COLUMNS(this.images, t)}
                fileName={t('discover.competitive_analysis.header')}
                extraBtns={extraBtns}
                additionalBox={additionalBox}
                hasTree={!this.state.view || this.state.view === 1}
                searchFunction={
                    !this.state.view || this.state.view === 1
                        ? (data, search) => this.searchFunction(this.state.data, search)
                        : null
                }
                header={t('discover.competitive_analysis.header')}
                forceRun={this.forceRun}
                defaultSortKey={'Own_impPerPage'}
                additionalStyles={{
                    Table: `
                        --data-table-library_grid-template-columns : minmax(200px, 4fr)  minmax(120px, 1fr) minmax(140px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(130px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) !important;
                        ${scrollForTable}
                    `,
                    BaseCell: ` 
                        font-family: 'Raleway';
                        font-weight: 700;
                        font-size: 16px;
                        line-height: 19px;
                        padding: 16px 10px !important;
                        color: #0D182C;
                    
                    
                        &.underline {
                            text-decoration: underline;
                        }
                    `,
                }}
            />
        );
    }
}

export default withTranslation()(Analysis);
